import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { push } from "react-router-redux";
import { Box, Button, ContextMenu, Icon, Text, metrocorpOperationsTestIds } from "@ui-kit/components/index.es";
import * as metrocorpMiddleware from "middleware/metrocorp";
import * as config from "util/config";
import * as i18n from "util/i18n";
import { actions as metrocorpActions } from "reducers/metrocorp";
import {
    isUxMetrocorpImprovementsEnabled,
    isUxMetrocorpMovementsEnabled,
    useEmbeddedV3ShellLayoutSync,
} from "util/v3ShellLayoutSync";
import {
    EXTRACTS_CALENDAR_YEARS_KEY,
    EXTRACTS_FLAG,
    GO_BACK_PATH,
    I18N_PREFIX,
    SCOPES,
    buildFutureValuesSection,
    buildHoldingsSection,
    defaultExtractMonth,
    downloadPerformanceFrom,
    extractAccountFromSubmit,
    extractHoldingsPayload,
    extractMinDate,
    getExtractTexts,
    getLabels,
    getNavigationFilters,
    holdingsRequestFromFilters,
    holdingsRequestFromNavigation,
    mapExtractAccountOptions,
    toIsoDate,
} from "./metrocorpOperationsV3Page.helpers";
import useMetrocorpMovements from "./useMetrocorpMovements";

/**
 * Mobile shell actions: same pattern as MetrocorpMain.renderContextMenu —
 * Resúmenes / Descargar live in a ContextMenu (bottom sheet), not as header icons.
 */
function buildMetrocorpMobileMenuOptions({
    extractsEnabled,
    onOpenExtracts,
    extractsDisabled,
    canDownload,
    onDownloadList,
}) {
    const menuOptions = [];
    if (extractsEnabled && onOpenExtracts) {
        menuOptions.push({
            label: getLabels("extracts"),
            callback: () => {
                if (!extractsDisabled) {
                    onOpenExtracts();
                }
            },
            icon: "Memo",
            iconVariant: "outline",
        });
    }
    if (canDownload && onDownloadList) {
        menuOptions.push({
            label: getLabels("download"),
            callback: onDownloadList,
            icon: "SendDown",
            iconVariant: "outline",
        });
    }
    return menuOptions;
}

function useHoldingsRequests() {
    const lastHoldingsRequestIdRef = useRef(0);
    const lastFutureValuesRequestIdRef = useRef(0);

    const handleHoldingsError = useCallback(
        (response) =>
            // Soft-fail: no redirigir a /error; la página queda con estado vacío
            // (p. ej. ESCO intermitente o sin tenencia). El toast/mensaje lo maneja el caller si aplica.
            response?.type === "W" || response?.data?.code === "API530W",
        [],
    );

    const invalidateInFlightRequests = useCallback(() => {
        lastHoldingsRequestIdRef.current += 1;
        lastFutureValuesRequestIdRef.current += 1;
    }, []);

    const requestHoldingsTab = useCallback(
        (params, setters, { asFilter = false } = {}) => {
            const requestId = lastHoldingsRequestIdRef.current + 1;
            lastHoldingsRequestIdRef.current = requestId;
            if (asFilter) {
                setters.setHoldingsFiltering(true);
            }
            return metrocorpMiddleware
                .listHoldings(params)
                .then((response) => {
                    if (requestId !== lastHoldingsRequestIdRef.current) {
                        return;
                    }
                    if (handleHoldingsError(response)) {
                        setters.setLoading(false);
                        setters.setHoldingsFiltering(false);
                        return;
                    }
                    setters.setHoldings(extractHoldingsPayload(response));
                    setters.setLoading(false);
                    setters.setHoldingsFiltering(false);
                })
                .catch(() => {
                    if (requestId !== lastHoldingsRequestIdRef.current) {
                        return;
                    }
                    setters.setLoading(false);
                    setters.setHoldingsFiltering(false);
                });
        },
        [handleHoldingsError],
    );

    const requestFutureValuesTab = useCallback(
        (params, setters, { asFilter = false } = {}) => {
            const requestId = lastFutureValuesRequestIdRef.current + 1;
            lastFutureValuesRequestIdRef.current = requestId;
            if (asFilter) {
                setters.setFutureValuesFiltering(true);
            } else {
                setters.setFutureValuesLoading(true);
            }
            return metrocorpMiddleware
                .listHoldings({ ...params, includeFutureValuesCount: true })
                .then((response) => {
                    if (requestId !== lastFutureValuesRequestIdRef.current) {
                        return;
                    }
                    if (handleHoldingsError(response)) {
                        setters.setFutureValuesLoading(false);
                        setters.setFutureValuesFiltering(false);
                        return;
                    }
                    setters.setFutureValues(extractHoldingsPayload(response));
                    setters.setFutureValuesLoading(false);
                    setters.setFutureValuesFiltering(false);
                })
                .catch(() => {
                    if (requestId !== lastFutureValuesRequestIdRef.current) {
                        return;
                    }
                    setters.setFutureValuesLoading(false);
                    setters.setFutureValuesFiltering(false);
                });
        },
        [handleHoldingsError],
    );

    return { handleHoldingsError, invalidateInFlightRequests, requestHoldingsTab, requestFutureValuesTab };
}

function useOperationsChrome(
    dispatch,
    {
        isDesktop,
        hideAmount,
        goBackPath,
        extractsEnabled,
        onOpenExtracts,
        extractsDisabled,
        canDownload,
        onDownloadList,
    },
) {
    const [showPartialAmounts, setShowPartialAmounts] = useState(false);
    const showPartialAmountsTimeout = useRef(null);
    const enableHideAmounts = config.getBoolean("frontend.hide.accounts.balance.funcionalities", false);
    const shouldHideAmounts = enableHideAmounts && hideAmount && !showPartialAmounts && !isDesktop;
    const mobileHeaderSubtitle = useMemo(
        () => (
            <Box flex align="center" justify="center" gap={8}>
                <Icon name="GraphLine" variant="outline" size={16} color="white-color" aria-hidden />
                <Text variant="body2" color="white" css={{ fontWeight: 500, fontSize: 14 }}>
                    Metrocorp
                </Text>
            </Box>
        ),
        [],
    );
    const handleShowBalances = useCallback(() => {
        setShowPartialAmounts(true);
        clearTimeout(showPartialAmountsTimeout.current);
        showPartialAmountsTimeout.current = setTimeout(() => {
            setShowPartialAmounts(false);
        }, 3000);
    }, []);
    const rightContentMobile = useMemo(() => {
        // Mirror MetrocorpMain.renderContextMenu + AccountDetailV3: overflow actions in a
        // ContextMenu (bottom sheet). Never place Resúmenes as a sibling of the eye icon.
        const hideAmountsButton =
            enableHideAmounts && hideAmount ? (
                <Button
                    variant="text"
                    color="white"
                    aria-label={i18n.get(`${I18N_PREFIX}.showBalances`)}
                    icon={!showPartialAmounts ? "EyeOff" : "Eye"}
                    iconVariant="outline"
                    onClick={handleShowBalances}
                />
            ) : null;
        const menuOptions = buildMetrocorpMobileMenuOptions({
            extractsEnabled,
            onOpenExtracts,
            extractsDisabled,
            canDownload,
            onDownloadList,
        });
        const contextMenu =
            menuOptions.length > 0 ? (
                <Box data-testid={metrocorpOperationsTestIds.extracts}>
                    <ContextMenu color="white" menuOptions={menuOptions} />
                </Box>
            ) : null;
        if (!hideAmountsButton && !contextMenu) {
            return null;
        }
        return (
            <Box flex align="center" gap={8}>
                {hideAmountsButton}
                {contextMenu}
            </Box>
        );
    }, [
        canDownload,
        enableHideAmounts,
        extractsDisabled,
        extractsEnabled,
        handleShowBalances,
        hideAmount,
        onDownloadList,
        onOpenExtracts,
        showPartialAmounts,
    ]);

    useEffect(() => {
        setShowPartialAmounts(false);
        clearTimeout(showPartialAmountsTimeout.current);
    }, [hideAmount]);

    useEffect(() => () => clearTimeout(showPartialAmountsTimeout.current), []);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell: true,
        isDesktop,
        titleKey: "menu.metrocorp.consultOperations",
        headerSubtitleElement: !isDesktop ? mobileHeaderSubtitle : null,
        goBackPath,
        scopes: SCOPES,
        rightContentMobile,
    });

    return { shouldHideAmounts };
}

function useSectionModels({
    dispatch,
    fetchingPerformance,
    shouldHideAmounts,
    holdings,
    futureValues,
    navigationFilters,
    holdingsFiltersActive,
    holdingsResetVersion,
    holdingsFiltering,
    futureValuesFiltersActive,
    futureValuesResetVersion,
    futureValuesFiltering,
    onFilterHoldings,
    onFilterFutureValues,
    onClearHoldingsFilters,
    onClearFutureValuesFilters,
}) {
    const onDownloadPerformance = useCallback(
        () => downloadPerformanceFrom(dispatch, holdings, holdings?.holdingsFilters?.date || holdings?.valuationDate),
        [dispatch, holdings],
    );
    const onDownloadFutureValuesPerformance = useCallback(
        () => downloadPerformanceFrom(dispatch, futureValues, toIsoDate(new Date())),
        [dispatch, futureValues],
    );
    const holdingsSection = useMemo(
        () =>
            buildHoldingsSection(holdings, {
                fetchingPerformance,
                shouldHideAmounts,
                onDownloadPerformance,
                navigationFilters,
                onFilterHoldings,
                holdingsFiltersActive,
                onClearHoldingsFilters,
                holdingsResetVersion,
                holdingsFiltering,
            }),
        [
            fetchingPerformance,
            holdings,
            holdingsFiltering,
            holdingsFiltersActive,
            holdingsResetVersion,
            navigationFilters,
            onClearHoldingsFilters,
            onDownloadPerformance,
            onFilterHoldings,
            shouldHideAmounts,
        ],
    );
    const futureValuesSection = useMemo(
        () =>
            buildFutureValuesSection(futureValues, {
                fetchingPerformance,
                shouldHideAmounts,
                onDownloadFutureValuesPerformance,
                onFilterFutureValues,
                futureValuesFiltersActive,
                onClearFutureValuesFilters,
                futureValuesResetVersion,
                futureValuesFiltering,
            }),
        [
            fetchingPerformance,
            futureValues,
            futureValuesFiltering,
            futureValuesFiltersActive,
            futureValuesResetVersion,
            onClearFutureValuesFilters,
            onDownloadFutureValuesPerformance,
            onFilterFutureValues,
            shouldHideAmounts,
        ],
    );
    return { holdingsSection, futureValuesSection };
}

function useHoldingsData({ location, metrocorpV3Enabled }) {
    const [holdings, setHoldings] = useState(null);
    const [futureValues, setFutureValues] = useState(null);
    const [loading, setLoading] = useState(true);
    const [holdingsFiltering, setHoldingsFiltering] = useState(false);
    const [futureValuesFiltering, setFutureValuesFiltering] = useState(false);
    const [futureValuesLoading, setFutureValuesLoading] = useState(true);
    const [holdingsResetVersion, setHoldingsResetVersion] = useState(0);
    const [futureValuesResetVersion, setFutureValuesResetVersion] = useState(0);
    const [navigationFilters, setNavigationFilters] = useState(() => getNavigationFilters(location));
    const [holdingsFiltersActive, setHoldingsFiltersActive] = useState(() => isHoldingsFilterActive(location));
    const [futureValuesFiltersActive, setFutureValuesFiltersActive] = useState(false);
    const {
        handleHoldingsError,
        invalidateInFlightRequests,
        requestHoldingsTab,
        requestFutureValuesTab,
    } = useHoldingsRequests();
    const holdingsSetters = { setHoldings, setLoading, setHoldingsFiltering };
    const futureSetters = { setFutureValues, setFutureValuesLoading, setFutureValuesFiltering };

    useEffect(() => {
        const navFilters = getNavigationFilters(location);
        setNavigationFilters(navFilters);
        setHoldingsFiltersActive(isHoldingsFilterActive(location));
    }, [location?.state]);

    useEffect(() => {
        if (!metrocorpV3Enabled) {
            return undefined;
        }
        const holdingsRequest = holdingsRequestFromNavigation(location?.state?.metrocorpHoldingsFilters);
        requestHoldingsTab(holdingsRequest, holdingsSetters);
        requestFutureValuesTab(
            {
                date: toIsoDate(new Date()),
                species: "all",
                ...holdingsRequest,
                includeFutureValuesCount: true,
            },
            futureSetters,
        );
        return () => {
            invalidateInFlightRequests();
        };
    }, [invalidateInFlightRequests, location?.state, metrocorpV3Enabled, requestFutureValuesTab, requestHoldingsTab]);

    const onFilterHoldings = useCallback(
        (values) => {
            setHoldingsFiltersActive(true);
            requestHoldingsTab(
                holdingsRequestFromFilters(values, holdings?.holdingsFilters?.accountOptions),
                holdingsSetters,
                { asFilter: true },
            );
        },
        [holdings, requestHoldingsTab],
    );
    const onFilterFutureValues = useCallback(
        (values) => {
            setFutureValuesFiltersActive(true);
            requestFutureValuesTab(
                holdingsRequestFromFilters(
                    { ...values, date: new Date() },
                    futureValues?.holdingsFilters?.accountOptions,
                ),
                futureSetters,
                { asFilter: true },
            );
        },
        [futureValues, requestFutureValuesTab],
    );
    const onClearHoldingsFilters = useCallback(() => {
        setNavigationFilters(undefined);
        setHoldingsFiltersActive(false);
        setHoldingsResetVersion((version) => version + 1);
        requestHoldingsTab({ species: "all", date: toIsoDate(new Date()) }, holdingsSetters, { asFilter: true });
    }, [requestHoldingsTab]);
    const onClearFutureValuesFilters = useCallback(() => {
        setFutureValuesFiltersActive(false);
        setFutureValuesResetVersion((version) => version + 1);
        requestFutureValuesTab({ species: "all", date: toIsoDate(new Date()) }, futureSetters, { asFilter: true });
    }, [requestFutureValuesTab]);

    return {
        handleHoldingsError,
        holdings,
        futureValues,
        loading,
        futureValuesLoading,
        navigationFilters,
        holdingsFiltersActive,
        holdingsResetVersion,
        holdingsFiltering,
        futureValuesFiltersActive,
        futureValuesResetVersion,
        futureValuesFiltering,
        onFilterHoldings,
        onFilterFutureValues,
        onClearHoldingsFilters,
        onClearFutureValuesFilters,
    };
}

function isHoldingsFilterActive(location) {
    const navFilters = getNavigationFilters(location);
    return Boolean(navFilters?.accountId || (navFilters?.speciesId && navFilters.speciesId !== "all"));
}

export function useMetrocorpOperationsPage({
    dispatch,
    fetchingPerformance,
    hideAmount,
    isDesktop,
    location,
    lastHrefPathname,
}) {
    const [section, setSection] = useState("holdings");
    const metrocorpV3Enabled = isUxMetrocorpImprovementsEnabled();
    const goBackPath = useMemo(() => {
        const fromState = location?.state?.return;
        if (typeof fromState === "string" && fromState.startsWith("/") && fromState !== "/") {
            return fromState;
        }
        if (
            typeof lastHrefPathname === "string" &&
            lastHrefPathname.startsWith("/") &&
            lastHrefPathname !== "/" &&
            lastHrefPathname !== location?.pathname
        ) {
            return lastHrefPathname;
        }
        return GO_BACK_PATH;
    }, [lastHrefPathname, location?.pathname, location?.state?.return]);
    const metrocorpMovementsEnabled = isUxMetrocorpMovementsEnabled();
    const extractsEnabled = config.getBoolean(EXTRACTS_FLAG, false);
    const data = useHoldingsData({ location, metrocorpV3Enabled });
    const extractAccountsOptions = useMemo(
        () => mapExtractAccountOptions(data.holdings?.holdingsFilters?.accountOptions),
        [data.holdings],
    );
    const extractsDisabled = extractAccountsOptions.length === 0;
    const onOpenExtracts = useCallback(() => {
        if (extractsDisabled) {
            return;
        }
        dispatch(metrocorpActions.setReportsDrawerVisible(true));
    }, [dispatch, extractsDisabled]);
    const onSetExtractsOpen = useCallback(
        (isVisible) => {
            dispatch(metrocorpActions.setReportsDrawerVisible(isVisible));
        },
        [dispatch],
    );
    const defaultExtractAccount = extractAccountsOptions[0]?.value;
    const extractDefaultMonth = useMemo(() => defaultExtractMonth(), []);
    const extractMinDateValue = useMemo(() => extractMinDate(config.getInteger(EXTRACTS_CALENDAR_YEARS_KEY, 5)), []);
    const onSubmitExtracts = useCallback(
        (formValues) => {
            dispatch(
                metrocorpActions.downloadMonthlyReport({
                    account: extractAccountFromSubmit(formValues?.account),
                    date: formValues?.month,
                    format: formValues?.format || "pdf",
                }),
            );
        },
        [dispatch],
    );
    const { shouldHideAmounts } = useOperationsChrome(dispatch, {
        isDesktop,
        hideAmount,
        goBackPath,
        extractsEnabled,
        onOpenExtracts,
        extractsDisabled,
        // Descargar (list export) uses legacy `/metrocorp.list` rows; V3 holdings are
        // KPI/UI-shaped. Slot is ready via buildMetrocorpMobileMenuOptions when wired.
        canDownload: false,
        onDownloadList: undefined,
    });
    const { holdingsSection, futureValuesSection } = useSectionModels({
        dispatch,
        fetchingPerformance,
        shouldHideAmounts,
        ...data,
    });
    const goBack = useCallback(() => dispatch(push(goBackPath)), [dispatch, goBackPath]);
    const { movementsSection, movementsLoading, movementsError } = useMetrocorpMovements({
        dispatch,
        section,
        holdings: data.holdings,
        holdingsSection,
        shouldHideAmounts,
        metrocorpV3Enabled,
        metrocorpMovementsEnabled,
        handleHoldingsError: data.handleHoldingsError,
    });

    return {
        section,
        setSection,
        goBack,
        getLabels,
        holdingsSection,
        futureValuesSection,
        metrocorpV3Enabled,
        metrocorpMovementsEnabled,
        movementsError,
        futureValuesCount: data.futureValues?.futureValuesCount || 0,
        movementsSection,
        extractsEnabled,
        onOpenExtracts: extractsEnabled ? onOpenExtracts : undefined,
        extractsDisabled,
        extractAccountsOptions,
        defaultExtractAccount,
        extractDefaultMonth,
        extractMinDate: extractMinDateValue,
        getExtractTexts,
        onSubmitExtracts,
        onSetExtractsOpen,
        showSpinner:
            data.loading ||
            (section === "futureValues" && data.futureValuesLoading && !data.futureValues) ||
            (section === "movements" && movementsLoading && !movementsSection),
    };
}

export default useMetrocorpOperationsPage;
